import { Link, useNavigate } from 'react-router-dom';
import type { ContractSummary } from '@/api/schemas';
import { ContractStatusBadge } from '@/components/ContractStatusBadge';
import { VALUE_DEFAULT_CLASS } from '@/components/Field';
import { PolicyDocumentOpenButton } from '@/components/PolicyDocumentOpenButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useClock } from '@/lib/clock';
import { deriveContractStatus } from '@/lib/contractStatus';
import { termInMonths } from '@/lib/contractTerm';
import { formatQuantity, NOT_AVAILABLE } from '@/lib/display';
import { DateText } from '@/lib/formatDate';

function MileageCoverage({
  contract,
  lastRecordedOdometer,
}: {
  contract: ContractSummary;
  lastRecordedOdometer: number | null | undefined;
}) {
  const start = contract.contractStartOdometer;
  const end = contract.contractEndOdometer;
  const coverage = end.value - start.value;

  if (coverage <= 0) {
    return NOT_AVAILABLE;
  }

  const remaining =
    lastRecordedOdometer != null && end.unit === 'miles'
      ? end.value - lastRecordedOdometer
      : null;

  return (
    <>
      {coverage.toLocaleString()}
      {remaining !== null ? (
        <span className="block text-[12px] leading-4 text-[rgba(0,0,0,0.81)]">
          ({remaining.toLocaleString()} left)
        </span>
      ) : null}
    </>
  );
}

function ContractRow({
  contract,
  lastRecordedOdometer,
  showVin,
}: {
  contract: ContractSummary;
  lastRecordedOdometer: number | null | undefined;
  showVin: boolean;
}) {
  const navigate = useNavigate();
  const today = useClock().today();
  const type = contract.coverageType ?? NOT_AVAILABLE;
  const contractPath = `/contracts/${contract.id}`;

  return (
    <TableRow
      className="cursor-pointer border-0 hover:bg-transparent [&>td]:h-16 [&>td]:border-y [&>td]:border-row-card-stroke [&>td]:bg-white [&>td:first-child]:rounded-l-[4px] [&>td:first-child]:border-l [&>td:last-child]:rounded-r-[4px] [&>td:last-child]:border-r"
      onClick={() => navigate(contractPath)}
    >
      <TableCell>
        <ContractStatusBadge status={deriveContractStatus(contract, today)} />
      </TableCell>
      {showVin ? (
        <TableCell className={VALUE_DEFAULT_CLASS}>{contract.vin}</TableCell>
      ) : null}
      <TableCell className={VALUE_DEFAULT_CLASS}>
        <Link
          to={contractPath}
          className="hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          {contract.policyNumber}
        </Link>
      </TableCell>
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`block truncate ${VALUE_DEFAULT_CLASS}`}>
              {type}
            </span>
          </TooltipTrigger>
          <TooltipContent>{type}</TooltipContent>
        </Tooltip>
      </TableCell>
      <TableCell onClick={(event) => event.stopPropagation()}>
        <PolicyDocumentOpenButton policyNumber={contract.policyNumber} />
      </TableCell>
      <TableCell className={VALUE_DEFAULT_CLASS}>
        <DateText value={contract.startDate} /> -{' '}
        <DateText value={contract.expiryDate} />
      </TableCell>
      <TableCell className={VALUE_DEFAULT_CLASS}>
        {termInMonths(contract.startDate, contract.expiryDate)} months
      </TableCell>
      <TableCell className={VALUE_DEFAULT_CLASS}>
        <MileageCoverage
          contract={contract}
          lastRecordedOdometer={lastRecordedOdometer}
        />
      </TableCell>
      <TableCell className={VALUE_DEFAULT_CLASS}>
        {formatQuantity(contract.contractEngineHoursLimit, 'hrs')}
      </TableCell>
    </TableRow>
  );
}

export function AssociatedContractsTable({
  contracts,
  lastRecordedOdometer,
  showVin = false,
}: {
  contracts: readonly ContractSummary[];
  lastRecordedOdometer: number | null | undefined;
  showVin?: boolean;
}) {
  return (
    <div className="overflow-x-auto pb-2">
      <Table className="table-fixed border-separate border-spacing-y-2">
        <TableHeader>
          <TableRow>
            <TableHead className="w-20">Status</TableHead>
            {showVin ? <TableHead className="w-44">VIN</TableHead> : null}
            <TableHead className="w-20">Policy</TableHead>
            <TableHead className="w-[320px]">Type</TableHead>
            <TableHead className="w-10">Policy Doc.</TableHead>
            <TableHead className="w-[120px]">Start &amp; End Date</TableHead>
            <TableHead className="w-[167px]">Terms</TableHead>
            <TableHead className="w-20">Mileage Coverage</TableHead>
            <TableHead className="w-24">Eng. Hrs Coverage</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contracts.map((contract) => (
            <ContractRow
              key={contract.id}
              contract={contract}
              lastRecordedOdometer={lastRecordedOdometer}
              showVin={showVin}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
